import { useLocation, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Home } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import { Header, LeadForm, Footer } from "@/pages/Landing";
import { CAT_BY_SLUG, CAT_LABEL } from "@/pages/categories";

const easeOut = [0.2, 0.7, 0.2, 1];

export default function CategoryPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0] || "";
  const cat = CAT_BY_SLUG[slug];
  const canonical = `https://ovamotorsparts.com/${slug}`;
  useSeo({
    title: cat?.title,
    description: cat?.description,
    canonical,
    keywords: cat?.keywords,
  });
  const jsonLd = useMemo(() => {
    if (!cat) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://ovamotorsparts.com/" },
        { "@type": "ListItem", position: 2, name: "Pièces auto", item: "https://ovamotorsparts.com/pieces-auto" },
        { "@type": "ListItem", position: 3, name: cat.breadcrumb, item: canonical },
      ],
    };
  }, [cat, canonical]);
  useJsonLd(`ld-${slug}`, jsonLd);
  if (!cat) return <Navigate to="/" replace />;

  return (
    <main data-testid={`category-page-${cat.slug}`} className="bg-[var(--ova-ink)] text-[var(--ova-off)] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative pt-[104px] pb-14 lg:pb-20 border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/45 mb-8">
            <Link to="/" className="hover:text-[var(--ova-yellow)] inline-flex items-center gap-1.5"><Home size={12}/> Accueil</Link>
            <ChevronRight size={12} />
            <Link to="/pieces-auto" className="hover:text-[var(--ova-yellow)]">Pièces auto</Link>
            {cat.slug !== "pieces-auto" && (<>
              <ChevronRight size={12} />
              <span className="text-white/80">{cat.breadcrumb}</span>
            </>)}
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easeOut }}
          >
            <div className="chapter mb-5">OVA Motors Parts · Sourcing spécialisé</div>
            <h1 className="font-display font-800 uppercase text-[38px] sm:text-[52px] lg:text-[72px] leading-[0.98] tracking-[-0.02em]">
              {cat.h1}<span className="text-[var(--ova-yellow)]">.</span>
            </h1>
            <p className="mt-6 max-w-3xl font-display text-[18px] lg:text-[22px] text-white/80 leading-[1.5]">
              {cat.hero}
            </p>
            <p className="mt-8 max-w-3xl text-[15px] leading-[1.75] text-white/60">
              {cat.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#demande" className="btn-yellow px-7 py-4 text-[12px] uppercase tracking-[0.18em] inline-flex items-center gap-2">
                Demander cette pièce <ArrowUpRight size={14} />
              </a>
              <Link to="/pieces-auto" className="btn-ghost-light px-7 py-4 text-[12px] uppercase tracking-[0.18em] inline-flex items-center gap-2">
                Voir tout notre sourcing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content sections */}
      <section className="py-16 lg:py-24 border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-14">
            {cat.sections.map((s, i) => (
              <motion.article
                key={s.h}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, ease: easeOut, delay: i * 0.06 }}
              >
                <div className="text-[var(--ova-yellow)] font-display font-800 text-[36px] leading-none tracking-tighter mb-4">0{i + 1}</div>
                <h2 className="font-display font-700 uppercase text-[24px] lg:text-[32px] tracking-tight mb-4">{s.h}</h2>
                <p className="text-[15px] leading-[1.8] text-white/65">{s.p}</p>
              </motion.article>
            ))}

            {/* Related links */}
            {cat.related && cat.related.length > 0 && (
              <div className="pt-10 border-t hairline">
                <h3 className="text-[11px] uppercase tracking-[0.28em] text-white/50 mb-5">Voir aussi</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {cat.related.map((rslug) => (
                    <li key={rslug}>
                      <Link
                        to={`/${rslug}`}
                        data-testid={`related-${rslug}`}
                        className="card-dark px-5 py-4 flex items-center justify-between text-[14px] font-600 hover:text-[var(--ova-yellow)] group"
                      >
                        <span>{CAT_LABEL[rslug]}</span>
                        <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform text-[var(--ova-yellow)]" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right rail — Lead form */}
          <aside className="lg:col-span-4" id="demande">
            <div className="lg:sticky lg:top-24">
              <LeadForm />
            </div>
          </aside>
        </div>
      </section>

      {/* Local zone note */}
      <section className="py-14 lg:py-20 bg-[var(--ova-panel)] border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="chapter mb-4">Zone d&apos;intervention</div>
          <h2 className="font-display font-700 uppercase text-[24px] lg:text-[36px] tracking-tight mb-4">
            Recherche & expédition depuis la <span className="text-[var(--ova-yellow)]">Guadeloupe</span>.
          </h2>
          <p className="max-w-3xl text-[14px] leading-[1.8] text-white/60">
            OVA Motors Parts est basé en Guadeloupe (971). Nous sourçons les pièces d&apos;occasion localement en priorité, puis auprès de notre réseau européen si nécessaire. Expédition organisée selon la pièce et la destination — nous vous confirmons les modalités au moment du devis.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
